// React
import React from "react";

// External libraries
import { faChevronUp, faChevronDown, faSearch, faLineColumns } from "@fortawesome/sharp-solid-svg-icons";

// Internal
import { ButtonGroup } from "../buttons/button-group";
import { getDataTestId } from "../functions/data-test-ids";
import { useHelpContext } from "../help-context";
import { InputString } from "../inputs/input-string";

// CSS
import "./panels.css";

const Markdown = React.lazy(() => import("../markdown/markdown"));

const ButtonMenuStyle = {
    List: 1,
    Tiles: 2
};

function PanelSpacer(props) {
    if (props.Visible === false) {
        return null;
    }

    return (
        <div style={{ minHeight: `${props.Height}px` }}></div>
    );
}

function PanelDivider(props) {
    if (props.Visible === false) {
        return null;
    }

    return (
        <div className="eone-ui-panel-divider"></div>
    );
}

function PanelSubheading(props) {
    if (props.Visible === false) {
        return null;
    }

    return (
        <div className="eone-ui-panel-sub-heading-container">
            {props.IncludeDivider && <PanelDivider />}
            <span className="eone-ui-panel-sub-heading-label">{props.Label}</span>
        </div>
    );
}


function PanelSection(props) {
    if (props.Visible === false) {
        return null;
    }

    return (
        <div className="eone-ui-panel-section-container">
            {props.children}
        </div>
    );
}

function PanelExplanationHelpLink(props) {
    const helpContext = useHelpContext();

    const navigateToHelp = () => {
        helpContext.push({ Id: props.Article, Type: "Article" });
    };

    if (!props.Article) {
        return null;
    }

    return (
        <div className="eone-ui-explanation-link-container">
            <button type="button" className="pd-link-primary eone-ui-explanation-link" onClick={navigateToHelp}>Read more</button>
        </div>
    );
}

function PanelExplanation(props) {
    if (!props.Explanation || !props.Explanation.Message) {
        return null;
    }
    return (<>
        <div className="eone-ui-explanation-container">
            <Markdown children={props.Explanation.Message} />
            <PanelExplanationHelpLink Article={props.Explanation.RelatedArticle} />
        </div>
        <PanelSpacer Height={20} />
    </>);
}

function PanelHeader(props) {
    const selectTableColumns = () => {
        if (typeof props.SelectTableColumns === "function") {
            props.SelectTableColumns();
        }
    };

    const toggleCollapse = () => {
        if (typeof props.Collapse === "function") {
            props.Collapse();
        }
    };

    const search = (value) => {
        if (typeof props.OnSearch === "function") {
            props.OnSearch(value);
        }
    };

    const getButtonClass = (button) => {
        return button.Tertiary ? "eone-ui-button-container-tertiary" : button.Primary ? "eone-ui-button-container-primary" : "eone-ui-button-container-secondary"
    }

    const getButtons = () => {
        const buttons = !props.Buttons ? [] : props.Buttons.map(button => ({ ...button, Class: getButtonClass(button) }));
        if (props.ShowSelectTableColumns) {
            buttons.push(
                {
                    Label: props.Labels?.SelectColumns || "Select columns",
                    Icon: faLineColumns,
                    DataTestId: "select-table-columns",
                    OnClick: selectTableColumns
                }
            );
        }
        if (props.ShowCollapse) {
            buttons.push({
                Label: props.Collapsed ? props.Labels?.Show || "Show" : props.Labels?.Hide || "Hide",
                Icon: props.Collapsed ? faChevronDown : faChevronUp,
                DataTestId: props.Collapsed ? "panel-show" : "panel-collapse",
                OnClick: toggleCollapse
            });
        }
        return buttons;
    };

    /*  NOTE - SC Changes: If props.Card === true then hide the header, as the title will be instead included in the PanelBody */ 
    if (props.Card || !props.Title || props.Title.trim() == "") {
        return null;
    }

    return (
        <div className="eone-ui-panel-heading-container">
            <h4 className="eone-ui-panel-heading">
                <span className="eone-ui-panel-heading-label" data-testid={getDataTestId(props.DataTestId, "title")}>{props.Title}</span>
            </h4>
            <div className="eone-ui-panel-header-controls-container">
                <ButtonGroup
                    Buttons={getButtons()}
                    DataTestId={props.DataTestId} />
                <InputString
                    Visible={props.Search === true}
                    Placeholder="Search"
                    Icon={faSearch}
                    Value={props.SearchValue}
                    OnChange={search}
                    DataTestId={getDataTestId(props.DataTestId, "search")} />
            </div>
        </div>
    );
}

function PanelBody(props) {
    if (props.Height !== undefined) {
        let divStyle = props.BodyStyle;
        if (divStyle) {
            divStyle.maxHeight = props.Height;
            divStyle.overflowY = "auto";
        } else {
            divStyle = { maxHeight: props.Height };
        }
        if (props.MinHeight) {
            divStyle.minHeight = props.MinHeight;
        }
        return (
            <div className={props.BodyClass} style={divStyle}>
                {props.children}
            </div>
        );
    }

    if(props.Card) {
        return (
            <div className={props.BodyClass} style={props.BodyStyle}>
                <div className="eone-ui-panel-body-card-header-container">
                    <label className="eone-ui-panel-sub-heading-label">
                        <span>{props.CardTitle}</span> 
                    </label>
                </div>
                {props.children}
            </div>
        );    
    }

    return (
        <div className={props.BodyClass} style={props.BodyStyle}>
            {props.children}
        </div>
    );
}

function Panel(props) {
    const selectTableColumns = () => {
        if (typeof props.SelectTableColumns === "function") {
            props.SelectTableColumns();
        }
    };

    const collapse = () => {
        if (typeof props.OnCollapse === "function") {
            props.OnCollapse();
        }
    };

    const clickHandler = (button) => {
        if (typeof props.OnClick === "function") {
            props.OnClick(button);
        }
    };

    const searchHandler = (value) => {
        if (typeof props.OnSearch === "function") {
            props.OnSearch(value);
        }
    };

    const filterHandler = (value) => {
        if (typeof props.OnFilter === "function") {
            props.OnFilter(value);
        }
    };

    const toggleCheckboxHandler = () => {
        if (typeof props.OnToggleCheckbox === "function") {
            props.OnToggleCheckbox();
        }
    };

    const getPanelClass = () => {
        let className = "eone-ui-panel";
        if (props.MaxHeightWithBreadcrumb) {
            className += " eone-ui-panel-max-height-with-breadcrumb";
        }
        if (props.PanelClass) {
            className += " " + props.PanelClass;
        }
        if(props.Card) {
            className += " eone-ui-panel-card" 
        }
        return className;
    };

    if (props.Visible !== undefined && !props.Visible) {
        return null;
    }

    if (props.Collapsible && props.Collapsed) {
        return (
            <div className="eone-ui-panel-collapsed">
                <PanelHeader
                    Collapsed={true}
                    Icon={props.Icon}
                    IconType={props.IconType}
                    IconStyle={props.IconStyle}
                    Title={props.Title}
                    ShowCollapse
                    Collapse={collapse}
                    DataTestId={props.DataTestId} />
            </div>
        );
    }

    return (
        <div className={getPanelClass()}>
            <PanelHeader
                ShowCollapse={props.Collapsible}
                Collapsed={props.Collapsed}
                Collapse={collapse}
                Icon={props.Icon}
                IconType={props.IconType}
                IconStyle={props.IconStyle}
                Title={props.Title}
                Search={props.Search}
                Filter={props.Filter}
                FilterOptions={props.FilterOptions}
                FilterValue={props.FilterValue}
                Checkbox={props.Checkbox}
                CheckboxLabel={props.CheckboxLabel}
                CheckboxValue={props.CheckboxValue}
                Buttons={props.HeaderButtons}
                ShowSelectTableColumns={props.ShowSelectTableColumns}
                SelectTableColumns={selectTableColumns}
                OnClick={clickHandler}
                OnSearch={searchHandler}
                SearchValue={props.SearchValue}
                OnFilter={filterHandler}
                OnToggleCheckbox={toggleCheckboxHandler}
                TooltipInfo={props.TooltipInfo}
                DataTestId={props.DataTestId} 
                Card={props.Card}/>
            <PanelExplanation Explanation={props.Explanation} />
            <PanelBody Card={props.Card} CardTitle={props.Title} BodyClass={props.BodyClass} BodyStyle={props.BodyStyle} Height={props.BodyHeight} MinHeight={props.BodyMinHeight}>
                {props.children}
            </PanelBody>
        </div>
    );
}

export { Panel, PanelHeader, PanelExplanation, ButtonMenuStyle, PanelSpacer, PanelSubheading, PanelSection };
